import { Dentist } from "@/services/dentist-service";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCPF, formatPhone, formatDateBR, formatCRO } from "@/utils/formatters";

interface DentistsTableProps {
  dentists: Dentist[];
  loading: boolean;
  onEdit: (dentist: Dentist) => void;
  onDelete: (dentist: Dentist) => void;
  onActivate: (dentist: Dentist) => void;
  onInactivate: (dentist: Dentist) => void;
}
export function DentistsTable({
  dentists,
  loading,
  onEdit,
  onDelete,
  onActivate,
  onInactivate,
}: DentistsTableProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (dentists.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhum profissional encontrado.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            <TableHead className="th">Nome</TableHead>
            <TableHead className="th">CPF</TableHead>
            <TableHead className="th">Data Nascimento</TableHead>
            <TableHead className="th">Email</TableHead>
            <TableHead className="th">Telefone</TableHead>
            <TableHead className="th">CRO</TableHead>
            <TableHead className="th">Status</TableHead>
            <TableHead className="th text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dentists.map((dentist) => (
            <TableRow key={dentist.id} className="hover:bg-muted/30 transition-colors">
              <TableCell className="td font-medium">{dentist.name}</TableCell>
              <TableCell className="td">{formatCPF(dentist.cpf)}</TableCell>
              <TableCell className="td">{formatDateBR(dentist.birth_date)}</TableCell>
              <TableCell className="td">{dentist.email}</TableCell>
              <TableCell className="td">{formatPhone(dentist.cellphone)}</TableCell>
              <TableCell className="td">{formatCRO(dentist.cro)}</TableCell>
              <TableCell className="td">
                <Badge
                  variant={dentist.status === 1 ? "default" : "secondary"}
                  className="text-xs py-0"
                >
                  {dentist.status === 1 ? "Ativo" : "Inativo"}
                </Badge>
              </TableCell>
              <TableCell className="td text-right">
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(dentist)}
                    className="h-7 w-7"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(dentist)}
                    className="h-7 w-7"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  {dentist.status === 1 ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onInactivate(dentist)}
                      className="h-7 w-7"
                      title="Inativar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onActivate(dentist)}
                      className="h-7 w-7"
                      title="Ativar"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
